import { SerializedStyles, css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { FC, useCallback, useRef } from 'react';

import Portal from '~ComponentsImproved/Portal';
import useOnClickOutside from '~Hooks/useOnClickOutside';
import { Box, Card, Dimmer, TransparentButton } from '~Styles';

export type CardinalOrigin = 'left' | 'right' | 'top' | 'bottom';

// Handle each origin
const positionStyle: Record<CardinalOrigin, SerializedStyles> = {
  top: css`
    top: 0;
    left: 0;
    right: 0;
    transform: translateY(-100%);
  `,
  right: css`
    bottom: 0;
    right: -8px;
    transform: translateX(100%);
  `,
  bottom: css`
    bottom: 0;
    left: 0;
    right: 0;
    transform: translateY(100%);
  `,
  left: css`
    bottom: 0;
    left: -8px;
    transform: translateX(-100%);
  `,
};

const PopoutContainer = styled(Box)<{
  open: boolean;
  width?: string;
  height?: string;
  position?: CardinalOrigin;
}>`
  overflow: auto;
  position: absolute;
  z-index: 99;
  min-width: 160px;
  ${({ position }) => positionStyle[position || 'right']}
  ${({ width }) => width && `width: ${width}`};
  ${({ height }) => height && `height: ${height}`};
  transition: transform 0.4s ease;
`;

const MenuCard = styled(Card)`
  & > :not(:last-child) {
    border-bottom: 1px solid var(--colorBlueGray200);
  }
`;

interface Props {
  position?: CardinalOrigin;
  open: boolean;
  onClose?: () => void;
  closeOnClickOutside?: boolean;
  children: React.ReactNode;
  rootId: string;
}

export const PopoutMenu: FC<Props> = ({
  position,
  open,
  onClose,
  closeOnClickOutside,
  children,
  rootId,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  // Handle clicking outside the modal
  const handleClickOutside = useCallback(() => {
    closeOnClickOutside && onClose && onClose();
  }, [closeOnClickOutside, onClose]);

  useOnClickOutside(ref, handleClickOutside);

  // If Popout isn't open, don't render it
  if (!open) return null;

  return (
    <Portal rootId={rootId}>
      <Dimmer
        amount="low"
        active={true}
        onTouchEnd={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />
      <PopoutContainer open={open} position={position}>
        <MenuCard
          ref={ref}
          background="colorWhite"
          padding="none"
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}>
          {children}
        </MenuCard>
      </PopoutContainer>
    </Portal>
  );
};

export const PopoutMenuItem = styled(TransparentButton)<{ disabled?: boolean; active?: boolean }>`
  ${({ disabled }) => disabled && 'opacity: 0.4'};
  display: flex;
  height: 57px;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.24px;
  text-transform: uppercase;
  color: var(--colorBlueGray1200);

  & > * {
    margin-right: 8px;
  }

  & > .SVG-icon {
    background-color: var(--colorBlueGray700);
  }
`;

PopoutMenuItem.defaultProps = {
  padding: 'medium',
};
